import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_joywin-photo-deals/artifacts/7835b5me_new%20logoo.PNG";
const JMAJOR_LOGO = "https://customer-assets-eiarnc6j.emergentagent.net/job_c5db327b-4bf4-420f-890a-b7ce4db2a362/artifacts/jdkdmeym_Jmajor-Photoroom.png";

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';
  const typeParam = searchParams.get('type');
  const isDJ = typeParam === 'dj';
  const { login } = useAuth();

  const accent = isDJ ? '#EF0000' : '#D4AF37';
  const currentLogo = isDJ ? JMAJOR_LOGO : LOGO_URL;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate(redirect);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="login-page"
      style={{ background: isDJ ? 'linear-gradient(135deg, #0a0000 0%, #1a0508 50%, #0a0000 100%)' : '#050505' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to={isDJ ? "/dj" : "/"}>
            <img 
              src={currentLogo} 
              alt={isDJ ? "J MAJOR" : "Joywin's Clicks"} 
              className="h-20 mx-auto object-contain"
            />
          </Link>
        </div>

        {/* Form Card */}
        <div className="bg-[#0a0a0a] rounded-2xl p-8 border border-white/10">
          <h1 className="font-['Playfair_Display'] text-2xl text-[#EDEDED] text-center mb-6">
            Welcome Back
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="pl-10 bg-[#121212] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="pl-10 pr-10 bg-[#121212] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#A1A1AA]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className={`w-full py-6 ${isDJ ? 'text-white' : 'gold-gradient-btn'}`}
              style={isDJ ? { background: '#EF0000' } : {}}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="text-center text-[#A1A1AA] text-sm mt-6">
            Don't have an account?{' '}
            <Link
              to={`/register${redirect !== '/' ? `?redirect=${redirect}` : ''}${isDJ ? (redirect !== '/' ? '&' : '?') + 'type=dj' : ''}`}
              className="hover:underline"
              style={{ color: accent }}
              data-testid="register-link"
            >
              Create one
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
